import { RollbackOutlined } from "@ant-design/icons";
import { Alert, Input, Modal, Radio, Typography } from "antd";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IReturnToCashRequest,
  RETURN_TO_CASH_REASON_MAX_LENGTH,
  RETURN_TO_CASH_REASON_MIN_LENGTH,
  RETURN_TO_CASH_REASON_TRANSLATION_KEYS,
  ReturnToCashReasonType,
} from "../../../models/order/return-to-cash";
import styles from "./style.module.scss";

const REASON_TYPES: ReturnToCashReasonType[] = [
  ReturnToCashReasonType.MISSED_PRODUCT_OR_QUANTITY,
  ReturnToCashReasonType.PRODUCT_OR_QUANTITY_NOW_AVAILABLE,
  ReturnToCashReasonType.CUSTOMER_REQUESTED_CHANGE,
  ReturnToCashReasonType.OTHER,
];

export interface IReturnToCashModalProps {
  open: boolean;
  loading?: boolean;
  isActiveTrip?: boolean;
  onSubmit: (request: IReturnToCashRequest) => void | Promise<void>;
  onCancel: () => void;
}

const ReturnToCashModal: React.FC<IReturnToCashModalProps> = ({
  open,
  loading = false,
  isActiveTrip = false,
  onSubmit,
  onCancel,
}) => {
  const { t, i18n } = useTranslation();
  const [reasonType, setReasonType] = useState<ReturnToCashReasonType>();
  const [reason, setReason] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const reasonGroupId = useId();
  const reasonInputId = useId();
  const isRtl = (i18n.language || "").startsWith("ar");
  const busy = loading || submitting;
  const trimmedReason = reason.trim();
  const isOtherReason = reasonType === ReturnToCashReasonType.OTHER;

  const reasonValidationKey = useMemo(() => {
    if (isOtherReason && trimmedReason.length === 0) {
      return "return_to_cash_reason_required";
    }

    if (
      trimmedReason.length > 0 &&
      trimmedReason.length < RETURN_TO_CASH_REASON_MIN_LENGTH
    ) {
      return "return_to_cash_reason_min_length";
    }

    if (trimmedReason.length > RETURN_TO_CASH_REASON_MAX_LENGTH) {
      return "return_to_cash_reason_max_length";
    }

    return undefined;
  }, [isOtherReason, trimmedReason]);

  const reasonTypeInvalid = submitAttempted && reasonType === undefined;
  const reasonInvalid = submitAttempted && Boolean(reasonValidationKey);

  useEffect(() => {
    if (open || busy) return;

    setReasonType(undefined);
    setReason("");
    setSubmitAttempted(false);
    submitLockRef.current = false;
  }, [busy, open]);

  const handleCancel = () => {
    if (busy) return;
    onCancel();
  };

  const handleSubmit = async () => {
    if (busy || isActiveTrip || submitLockRef.current) return;

    setSubmitAttempted(true);
    if (reasonType === undefined || reasonValidationKey) return;

    submitLockRef.current = true;
    setSubmitting(true);
    try {
      await onSubmit({
        reasonType,
        reason: trimmedReason || null,
      });
    } catch {
      // The parent and shared HTTP interceptor own error handling. Keeping the
      // modal open preserves the operator's selected reason and note for retry.
    } finally {
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <Modal
      className={styles.modal}
      title={
        <span className={styles.title}>
          <RollbackOutlined aria-hidden />
          <span>{t("return_to_cash_title")}</span>
        </span>
      }
      open={open}
      onOk={handleSubmit}
      onCancel={handleCancel}
      okText={t("return_to_cash_confirm")}
      cancelText={t("cancel")}
      confirmLoading={busy}
      okButtonProps={{ disabled: busy || isActiveTrip }}
      cancelButtonProps={{ disabled: busy }}
      closable={!busy}
      keyboard={!busy}
      maskClosable={false}
      width={600}
      destroyOnClose={false}
    >
      <div className={styles.content} dir={isRtl ? "rtl" : "ltr"}>
        <Alert
          type={isActiveTrip ? "error" : "warning"}
          showIcon
          message={
            isActiveTrip
              ? t("return_to_cash_active_trip_blocked")
              : t("return_to_cash_warning")
          }
        />

        <fieldset
          className={styles.reasonFieldset}
          disabled={busy || isActiveTrip}
          aria-describedby={
            reasonTypeInvalid ? `${reasonGroupId}-error` : undefined
          }
        >
          <legend className={styles.legend} id={reasonGroupId}>
            {t("return_to_cash_reason_type")}
            <Typography.Text
              className={styles.requiredMark}
              type="danger"
              aria-hidden
            >
              *
            </Typography.Text>
          </legend>

          <Radio.Group
            className={styles.reasonList}
            value={reasonType}
            aria-labelledby={reasonGroupId}
            aria-invalid={reasonTypeInvalid}
            onChange={(event) =>
              setReasonType(event.target.value as ReturnToCashReasonType)
            }
          >
            {REASON_TYPES.map((type) => (
              <Radio className={styles.reasonOption} value={type} key={type}>
                {t(RETURN_TO_CASH_REASON_TRANSLATION_KEYS[type])}
              </Radio>
            ))}
          </Radio.Group>

          {reasonTypeInvalid ? (
            <Typography.Text
              className={styles.errorText}
              id={`${reasonGroupId}-error`}
              type="danger"
              role="alert"
            >
              {t("return_to_cash_reason_type_required")}
            </Typography.Text>
          ) : null}
        </fieldset>

        <div className={styles.noteField}>
          <div className={styles.fieldHeader}>
            <label className={styles.fieldLabel} htmlFor={reasonInputId}>
              {t("return_to_cash_reason_details")}
              {isOtherReason ? (
                <Typography.Text
                  className={styles.requiredMark}
                  type="danger"
                  aria-hidden
                >
                  *
                </Typography.Text>
              ) : null}
            </label>
            {!isOtherReason ? (
              <Typography.Text type="secondary">
                {t("return_to_cash_optional")}
              </Typography.Text>
            ) : null}
          </div>

          <Input.TextArea
            id={reasonInputId}
            className={styles.textarea}
            value={reason}
            rows={4}
            maxLength={RETURN_TO_CASH_REASON_MAX_LENGTH}
            showCount
            disabled={busy || isActiveTrip}
            status={reasonInvalid ? "error" : undefined}
            aria-required={isOtherReason}
            aria-invalid={reasonInvalid}
            aria-describedby={
              reasonInvalid ? `${reasonInputId}-error` : undefined
            }
            placeholder={t("return_to_cash_reason_placeholder")}
            onChange={(event) => setReason(event.target.value)}
          />

          {reasonInvalid && reasonValidationKey ? (
            <Typography.Text
              className={styles.errorText}
              id={`${reasonInputId}-error`}
              type="danger"
              role="alert"
            >
              {t(reasonValidationKey, {
                min: RETURN_TO_CASH_REASON_MIN_LENGTH,
                max: RETURN_TO_CASH_REASON_MAX_LENGTH,
              })}
            </Typography.Text>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default ReturnToCashModal;
